import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { debounce, capitalize } from 'lodash'
import { CanceledError } from 'axios'
import { nanoid } from 'nanoid'
import { TableStore, toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { RequestCancelAxios } from 'shared/api/lib'
import {
  type Campaign,
  CampaignApi,
  type IParamsCampaignExecutionFilter,
  type ICreateCampaignStep,
  type IParamsExecutions,
  type IResponseExecutions,
  type IParamsCampaignsByIdMessagesList,
  type IResponseCampaignsByIdMessages,
  type IResponseCampaignsByIdQuietHoursMessages,
} from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { CampaignExecution } from 'pages/campaigns/ui/detail/CampaignContactsStatisticsTable/model/CampaignExecution'
import { CampaignFailedMessage } from 'pages/campaigns/ui/detail/CampaignContactsStatisticsTable/model/CampaignFailedMessage'
import { CampaignQuietHoursMessage } from 'pages/campaigns/ui/detail/CampaignContactsStatisticsTable/model/CampaignQuietHoursMessage'
import { type ICampaignModelTable } from 'pages/campaigns/ui/detail/CampaignContactsStatisticsTable/model/types'

export class CampaignContactsStatisticsTableStore {
  private _tableStore = new TableStore<ICampaignModelTable>({
    element: 'contact',
    withoutDefaultManageColumns: true,
  })
  private readonly _contactsTagsModalStore: ContactsTagsModalStore
  private _contactsDetailsManageStore: ContactsDetailsManageStore
  private readonly _disposeParamsGetItems
  private readonly _disposeParamsWithoutPage
  private _filterState: IParamsCampaignExecutionFilter | null = null
  private _limit = 25
  private _page = 1
  private _search = ''
  private _total = 0
  private _scrollToTopTrigger = ''
  private _loading = true
  private _cancelRequest = new RequestCancelAxios()
  private _itemsMap: Map<number, ICampaignModelTable> = new Map()
  private _debounceLoadData: ReturnType<typeof debounce>

  constructor(
    private campaign: Campaign,
    private _entities: IEntitiesStore,
    private _step?: ICreateCampaignStep
  ) {
    this.loadData()

    this._debounceLoadData = debounce(this.loadData, 500)
    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => {
        this.setLoading(true)

        this._cancelRequest.reset()
        this._debounceLoadData()
      }
    )
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this.setLoading(true)
        this._page = 1
      }
    )
    makeAutoObservable(this)
  }

  dispose = () => {
    this._disposeParamsGetItems()
    this._disposeParamsWithoutPage()
  }

  setFilterState = (value: IParamsCampaignExecutionFilter | null) => {
    this._filterState = value
  }

  get paramsWithoutPage(): Omit<IParamsExecutions, 'page'> {
    return {
      id: this.campaign.id,
      limit: this._limit,
      'filters[state]': this._filterState || undefined,
      'filters[step_id]': this._step?.id || undefined,
    }
  }

  get paramsGetItems(): IParamsExecutions {
    return {
      ...this.paramsWithoutPage,
      page: this._page,
    }
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  get hasFailedMessagesFilter() {
    return this._filterState === 'failed-messages'
  }

  get hasQuietHoursFilter() {
    return this._filterState === 'quiet_hours'
  }

  private loadData = async () => {
    try {
      this._cancelRequest.init()

      this.setLoading(true)

      if (this.hasFailedMessagesFilter || this.hasQuietHoursFilter) {
        const params: IParamsCampaignsByIdMessagesList = {
          limit: this.paramsGetItems.limit,
          page: this.paramsGetItems.page,
        }
        const responseData = this.hasFailedMessagesFilter
          ? await CampaignApi.getCampaignsByIdFailedMessages(this.campaign.id, params, {
              cancelToken: this._cancelRequest.token,
            })
          : await CampaignApi.getCampaignsByIdQuietHoursMessages(this.campaign.id, params, {
              cancelToken: this._cancelRequest.token,
            })
        const data = responseData.data

        this._setData(data)
      } else {
        const responseData = await CampaignApi.getExecutions(this.paramsGetItems, {
          cancelToken: this._cancelRequest.token,
        })
        const data = responseData.data

        this._setData(data)
      }

      this.setLoading(false)
    } catch (e) {
      logger.error(e)
      this.setLoading(e instanceof CanceledError)
    }
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  private _setData(
    payload:
      | IResponseExecutions
      | IResponseCampaignsByIdMessages
      | IResponseCampaignsByIdQuietHoursMessages
  ) {
    const { data, meta } = payload
    this._itemsMap.clear()
    data.forEach((item) => {
      if ('failed_reason' in item) {
        this._itemsMap.set(item.id, new CampaignFailedMessage(item))
      } else if ('send_at' in item) {
        this._itemsMap.set(item.id, new CampaignQuietHoursMessage(item))
      } else {
        this._itemsMap.set(item.id, new CampaignExecution(item))
      }
    })
    this._total = meta.total
    this._scrollToTopTrigger = nanoid()
  }

  onSuccessDelete = () => {
    this._tableStore.unselectAllIds()
    void this.loadData()
  }

  removeExecutions = (campaignExecutions: ICampaignModelTable[]) => {
    const modalId = nanoid()
    const contactsString = `contact${campaignExecutions.length > 1 ? 's' : ''}`
    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.INFO,
      title: `Remove ${campaignExecutions.length} ${contactsString} from campaign?`,
      desc: `Removed ${contactsString} will no longer receive messages from this campaign`,
      primaryAction: {
        text: 'Remove',
        onAction: async () => {
          try {
            modalStore.addDisabledRemoveId(modalId)
            const executionsIds = campaignExecutions.map((item) => item.id)
            await CampaignApi.removeExecutions({
              campaignId: this.campaign.id,
              executionsIds: executionsIds,
            })
            this.campaign.refresh()
            this.loadData().then(() => {
              if (this._page > 1 && !this._itemsMap.size) {
                runInAction(() => {
                  this._page = 1
                })
              }
            })
            this._tableStore.resetSelected()

            modalStore.removeDisabledRemoveId(modalId)
            modalStore.removeModal(modalId)
            toastStore.add({
              id: nanoid(),
              type: 'success',
              title: `${capitalize(contactsString)} removed from campaign`,
            })
          } catch (e) {
            console.error(e)
          } finally {
            modalStore.removeDisabledRemoveId(modalId)
          }
        },
      },
      closeActionButtonProps: {
        contained: 'secondary',
      },
    })
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: async (data) => {
        this._contactsDetailsManageStore.setDisabled(true)
        try {
          await this._entities.inboxesStore.fetchInboxes()
          const sharedInboxes = this._entities.inboxesStore.sharedInboxes

          const inboxId = sharedInboxes.find((inbox) => inbox.id === data.inboxId)?.id

          if (!inboxId) {
            modalStore.addModal({
              disabledOnAllClose: true,
              type: ModalTypeList.INFO,
              title: 'Content unavailable',
              id: 'content_unavailable',
              desc: "We're sorry, but you don't currently have access to this content or it was deleted. Please contact the user who shared the link with you to request access.",
            })

            return
          }

          await this._contactsDetailsManageStore.onOpenContactDetails(
            {
              contactId: data.contactId,
              inboxId,
            },
            'campaign',
            true
          )
        } finally {
          this._contactsDetailsManageStore.setDisabled(false)
        }
      },
    }
  }

  get isNoData() {
    return !this._loading && !this._itemsMap.size && !this._search
  }

  get variant(): 'overview' | 'step' {
    if (this._step) {
      return 'step'
    }
    return 'overview'
  }

  get isShowReply() {
    return this.variant === 'overview' && !!this.campaign.settings?.unenroll_on_response
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get status() {
    return this.campaign.status
  }

  get total() {
    return this._total
  }

  get loading() {
    return this._loading
  }

  get tableStore() {
    return this._tableStore
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  get scrollToTopTrigger() {
    return this._scrollToTopTrigger
  }

  get page() {
    return this._page
  }

  get limit() {
    return this._limit
  }

  get search() {
    return this._search
  }
}
